/**
 * Core encoding/decoding operation types
 * Based on data model from specs/003-encoders-decoders-tools/data-model.md
 */

export const EncoderToolType = {
  BASE64_TEXT: 'base64-text',
  BASE64_IMAGE: 'base64-image',
  QR_CODE: 'qr-code',
  URL_ENCODER: 'url-encoder',
  JWT_TOKEN: 'jwt-token',
  HTML_ENTITY: 'html-entity',
  GZIP_COMPRESSION: 'gzip-compression',
  CERTIFICATE_DECODER: 'certificate-decoder',
  JSON_FORMATTER: 'json-formatter',
  SQL_FORMATTER: 'sql-formatter',
  XML_FORMATTER: 'xml-formatter',
  HASH_GENERATOR: 'hash-generator',
  LOREM_IPSUM: 'lorem-ipsum',
  PASSWORD_GENERATOR: 'password-generator',
  UUID_GENERATOR: 'uuid-generator',
  COLOR_BLINDNESS_SIMULATOR: 'color-blindness-simulator',
  IMAGE_CONVERTER: 'image-converter',
  JSONPATH_TESTER: 'jsonpath-tester',
  REGEX_TESTER: 'regex-tester',
  XML_TESTER: 'xml-tester',
  TEXT_ESCAPE: 'text-escape',
  LIST_COMPARE: 'list-compare',
  MARKDOWN_PREVIEW: 'markdown-preview',
  TEXT_ANALYZER: 'text-analyzer',
  TEXT_COMPARE: 'text-compare'
} as const;

export type EncoderToolType = typeof EncoderToolType[keyof typeof EncoderToolType];

export const CharacterEncoding = {
  UTF8: 'utf-8',
  ASCII: 'ascii',
  UTF16: 'utf-16',
  LATIN1: 'latin1'
} as const;

export type CharacterEncoding = typeof CharacterEncoding[keyof typeof CharacterEncoding];

export const ErrorCorrectionLevel = {
  LOW: 'L',
  MEDIUM: 'M',
  QUARTILE: 'Q',
  HIGH: 'H'
} as const;

export type ErrorCorrectionLevel = typeof ErrorCorrectionLevel[keyof typeof ErrorCorrectionLevel];

export const UrlSpaceHandling = {
  Encode: 'encode',
  Plus: 'plus',
  Preserve: 'preserve'
} as const;

export type UrlSpaceHandling = typeof UrlSpaceHandling[keyof typeof UrlSpaceHandling];

export const UrlCaseHandling = {
  Preserve: 'preserve',
  Uppercase: 'uppercase',
  Lowercase: 'lowercase'
} as const;

export type UrlCaseHandling = typeof UrlCaseHandling[keyof typeof UrlCaseHandling];

export const ImageFormat = {
  Base64: 'base64',
  DataURL: 'data-url',
  HTML: 'html',
  CSS: 'css'
} as const;

export type ImageFormat = typeof ImageFormat[keyof typeof ImageFormat];

export const CertificateFormat = {
  PEM: 'pem',
  DER: 'der',
  BASE64: 'base64',
  PKCS12: 'pkcs12',
  PKCS7: 'pkcs7'
} as const;

export type CertificateFormat = typeof CertificateFormat[keyof typeof CertificateFormat];

export const CertificateMode = {
  DECODE: 'decode',
  ENCODE: 'encode'
} as const;

export type CertificateMode = typeof CertificateMode[keyof typeof CertificateMode];

export interface CertificateConfiguration {
  input: string | Uint8Array;
  mode: CertificateMode;
  inputFormat?: CertificateFormat;
  outputFormat?: CertificateFormat;
  includeExtensions?: boolean;
  validateSignature?: boolean;
  checkRevocation?: boolean;
  password?: string; // For PKCS12 files
}

export interface ImageResizeOptions {
  width?: number;
  height?: number;
  maintainAspectRatio?: boolean;
}

export interface Base64ImageConfiguration {
  id: string;
  operation: 'encode' | 'decode';
  inputFile?: File;
  inputBase64?: string;
  outputFormat?: 'png' | 'jpg' | 'jpeg' | 'webp' | 'gif';
  quality?: number;
  maxWidth?: number;
  maxHeight?: number;
  preserveAspectRatio?: boolean;
  processedAt: Date;
}

export interface EncodingOperation {
  id: string;
  toolType: EncoderToolType;
  operationType: 'encode' | 'decode' | 'analyze' | 'convert' | 'compare' | 'convert-case' | 'normalize-whitespace' | 'escape' | 'unescape';
  inputData: string | File;
  outputData?: string | File | Uint8Array | Record<string, any> | null;
  timestamp: Date;
  status: 'pending' | 'success' | 'error';
  errorMessage?: string;
  metadata?: Record<string, any>;
}